import { Skeleton } from "components/ui/skeleton";

export function LeadCardSkeleton() {
    return (
        <div className="border border-line dark:border-dark-700 rounded-xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow fade-in w-full max-w-full min-w-0 overflow-hidden">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div className="flex flex-wrap items-center gap-2.5 min-w-0 flex-1">
                    {/* checkbox */}
                    <Skeleton className="h-4 w-4 rounded-sm flex-shrink-0" />
                    {/* status dot */}
                    <Skeleton className="h-3.5 w-3.5 rounded-full flex-shrink-0" />
                    {/* name + email */}
                    <div className="min-w-0 flex-1 sm:flex-initial">
                        <Skeleton variant="text" className="!h-4 w-28 sm:w-40 mb-2 max-w-full" />
                        <Skeleton variant="text" className="!h-3 w-36 sm:w-56 max-w-full" />
                    </div>
                    {/* status pill */}
                    <Skeleton variant="chip" className="h-6 w-16 flex-shrink-0" />
                    {/* AI badge (optional) */}
                    <Skeleton variant="chip" className="h-6 w-20 flex-shrink-0" />
                </div>

                {/* actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    <Skeleton className="h-8 w-8 rounded-lg" />
                    <Skeleton className="h-8 w-8 rounded-lg" />
                    <Skeleton className="h-8 w-8 rounded-lg" />
                </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="min-w-0">
                    <Skeleton variant="text" className="!h-3 w-20 mb-2 max-w-full" />
                    <Skeleton variant="text" className="!h-4 w-12 mb-2 max-w-full" />
                    {/* progress bar */}
                    <div className="w-full bg-gray-100 dark:bg-dark-700 rounded-full h-2 mt-1 overflow-hidden">
                        <Skeleton className="h-2 w-1/2 rounded-full" />
                    </div>
                </div>
                <div className="min-w-0">
                    <Skeleton variant="text" className="!h-3 w-16 mb-2 max-w-full" />
                    <Skeleton variant="text" className="!h-4 w-20 max-w-full" />
                </div>
                <div className="min-w-0">
                    <Skeleton variant="text" className="!h-3 w-20 mb-2 max-w-full" />
                    <Skeleton variant="text" className="!h-4 w-24 max-w-full" />
                </div>
                <div className="min-w-0">
                    <Skeleton variant="text" className="!h-3 w-16 mb-2 max-w-full" />
                    <Skeleton variant="text" className="!h-4 w-20 max-w-full" />
                </div>
            </div>

            {/* Tags */}
            <div className="flex items-center gap-2 flex-wrap mb-4">
                <Skeleton variant="chip" className="h-5 w-14 flex-shrink-0" />
                <Skeleton variant="chip" className="h-5 w-12 flex-shrink-0" />
                <Skeleton variant="chip" className="h-5 w-16 flex-shrink-0" />
                <Skeleton variant="chip" className="h-5 w-10 flex-shrink-0" />
            </div>

            {/* Contact Info */}
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                    <Skeleton className="h-4 w-4 rounded flex-shrink-0" />
                    <Skeleton variant="text" className="!h-3 w-24 max-w-full" />
                </div>
                <div className="flex items-center gap-2 min-w-0">
                    <Skeleton className="h-4 w-4 rounded flex-shrink-0" />
                    <Skeleton variant="text" className="!h-3 w-20 max-w-full" />
                </div>
            </div>
        </div>
    );
}

export function LeadCardGridSkeleton({ count = 8 }: { count?: number }) {
    return (
        <div
            id="lead-grid-skeleton"
            className="space-y-4"
            role="grid"
            aria-label="Loading leads"
        >
            {Array.from({ length: count }).map((_, i) => (
                <LeadCardSkeleton key={i} />
            ))}
        </div>
    );
}

export function LeadCardListSkeleton({ count = 8 }: { count?: number }) {
    return (
        <div className="space-y-2" role="grid" aria-label="Loading leads">
            <div className="hidden sm:grid grid-cols-[auto_auto_minmax(180px,1.5fr)_minmax(120px,1fr)_minmax(120px,1fr)_48px] md:grid-cols-[auto_auto_minmax(220px,1.5fr)_minmax(140px,1fr)_minmax(140px,1fr)_48px] xl:grid-cols-[auto_auto_minmax(260px,1.5fr)_minmax(160px,1fr)_minmax(160px,1fr)_48px] gap-x-4 xl:gap-x-6 pl-5 pr-4 pb-1">
                <Skeleton variant="text" className="!h-3 w-0" />
                <Skeleton variant="text" className="!h-3 w-0" />
                <Skeleton variant="text" className="!h-3 w-16" />
                <Skeleton variant="text" className="!h-3 w-12" />
                <Skeleton variant="text" className="!h-3 w-12 justify-self-end" />
                <Skeleton variant="text" className="!h-3 w-10 justify-self-end" />
            </div>
            {Array.from({ length: count }).map((_, i) => (
                <div
                    key={i}
                    className="grid grid-cols-[auto_auto_minmax(0,1fr)_auto] sm:grid-cols-[auto_auto_minmax(180px,1.5fr)_minmax(120px,1fr)_minmax(120px,1fr)_48px] md:grid-cols-[auto_auto_minmax(220px,1.5fr)_minmax(140px,1fr)_minmax(140px,1fr)_48px] xl:grid-cols-[auto_auto_minmax(260px,1.5fr)_minmax(160px,1fr)_minmax(160px,1fr)_48px] items-center gap-x-4 xl:gap-x-6 gap-y-2 pl-5 pr-4 py-3 rounded-xl border border-line dark:border-dark-700 bg-surface dark:bg-dark-800"
                >
                    <Skeleton className="h-4 w-4 rounded-sm" />
                    <Skeleton className="h-2 w-2 rounded-full" />
                    <div className="min-w-0">
                        <Skeleton variant="text" className="!h-4 w-24 sm:w-32 mb-2" />
                        <Skeleton variant="text" className="!h-3 w-36 sm:w-44 max-w-full" />
                    </div>
                    <div className="hidden sm:block min-w-0">
                        <Skeleton variant="text" className="!h-4 w-24 mb-2 max-w-full" />
                        <Skeleton variant="text" className="!h-3 w-10" />
                    </div>
                    <div className="hidden sm:block min-w-0 justify-self-end">
                        <Skeleton variant="text" className="!h-4 w-24 mb-2" />
                        <Skeleton variant="text" className="!h-3 w-10 ml-auto" />
                    </div>
                    <Skeleton className="h-7 w-7 rounded-lg justify-self-end" />
                    <div className="col-start-3 col-span-2 sm:hidden grid grid-cols-[minmax(0,1fr)_auto] gap-3">
                        <Skeleton variant="text" className="!h-3 w-24 max-w-full" />
                        <Skeleton variant="text" className="!h-3 w-16" />
                    </div>
                </div>
            ))}
        </div>
    );
}



//  *************** LEADS GROUP SKELETON ***************
export function LeadsGroupSkeleton() {
    return (
        <div className="border rounded-lg p-4 bg-surface shadow-sm hover:shadow-md transition-all fade-in">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3 flex-1">
                    {/* checkbox */}
                    <Skeleton className="h-4 w-4 rounded-sm mt-1" />
                    <div className="flex-1">
                        {/* name row */}
                        <div className="flex items-center gap-2 mb-1">
                            <Skeleton variant="text" className="!h-4 !w-40" />
                            {/* high performing star (optional) */}
                            <Skeleton className="h-4 w-4 rounded" />
                        </div>
                        {/* description */}
                        <Skeleton variant="text" className="!h-3 !w-64 mb-1" />
                        <Skeleton variant="text" className="!h-3 !w-48" />
                    </div>
                </div>

                <div className="flex items-center gap-1">
                    {/* AI badge (optional) */}
                    <Skeleton variant="chip" className="h-6 w-14" />
                    {/* menu button */}
                    <Skeleton className="h-8 w-8 rounded" />
                </div>
            </div>

            {/* Metrics */}
            <div className="flex items-center justify-between">
                {/* leads count */}
                <Skeleton variant="text" className="!h-6 !w-16" />
                {/* status chip */}
                <Skeleton variant="chip" className="h-6 w-20" />
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-1 mt-3">
                <Skeleton variant="chip" className="h-5 w-16" />
                <Skeleton variant="chip" className="h-5 w-14" />
                <Skeleton variant="chip" className="h-5 w-20" />
            </div>
        </div>
    );
}

export function LeadsGroupGridSkeleton({ count = 8 }: { count?: number }) {
    return (
        <div
            id="segment-grid-skeleton"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            role="grid"
            aria-label="Loading segments"
        >
            {Array.from({ length: count }).map((_, i) => (
                <LeadsGroupSkeleton key={i} />
            ))}
        </div>
    );
}



/*******     Edit Leads Skeleton              **** */
export function LeadFormSkeleton() {
    return (
        <>
            {/* Header */}
            <div className="mb-4 flex items-center gap-3">
                <Skeleton className="h-9 w-9 rounded-lg" />
                <Skeleton variant="text" className="!h-6 !w-40" />
            </div>

            {/* Card */}
            <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
                {/* Fields Grid */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FieldSkeleton labelWidth="w-28" inputWidth="w-full" />
                    <FieldSkeleton labelWidth="w-28" inputWidth="w-full" />
                    <FieldSkeleton labelWidth="w-24" inputWidth="w-full" />
                    <FieldSkeleton labelWidth="w-20" inputWidth="w-full" />
                    <FieldSkeleton labelWidth="w-28" inputWidth="w-full" />
                    <FieldSkeleton labelWidth="w-36" inputWidth="w-full" />
                    {/* Messenger (full width) */}
                    <div className="md:col-span-2">
                        <FieldSkeleton labelWidth="w-32" inputWidth="w-full" />
                    </div>
                </div>

                {/* Footer actions */}
                <div className="mt-6 flex items-center justify-end gap-3 border-t border-line-subtle pt-4">
                    <Skeleton className="h-9 w-24 rounded-lg" />
                    <Skeleton className="h-9 w-40 rounded-lg" />
                </div>
            </div>
        </>
    );
}

function FieldSkeleton({
    labelWidth = "w-24",
    inputWidth = "w-full",
}: {
    labelWidth?: string;
    inputWidth?: string;
}) {
    return (
        <div className="space-y-2">
            <Skeleton variant="text" className={`!h-4 ${labelWidth}`} />
            <Skeleton className={`h-10 ${inputWidth} rounded-lg`} />
            {/* inline helper/error line */}
            <Skeleton variant="text" className="!h-3 !w-32" />
        </div>
    );
}
